import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getMediaUrl } from '../../services/api';
import GalleryGridEditor from '../GalleryGridEditor';
import useMediaFiltering from '../../hooks/useMediaFiltering';
import useCreditsTab from '../../hooks/useCreditsTab';
import useGalleryConfig from '../../hooks/useGalleryConfig';
import VisibilityToggle from './VisibilityToggle';
import './BoldTemplate.css';

function BoldTemplate({
  profileData,
  basicUser,
  isOwnProfile,
  onEdit,
  onPhotoClick,
  galleryEditMode,
  onToggleGalleryEdit,
  onGalleryConfigChange,
  onGalleryReorder,
  galleryConfig,
  onGalleryPhotoCrop,
  hiddenSections = [],
  onToggleSection,
}) {
  const [activeSection, setActiveSection] = useState('home');
  const sectionRefs = useRef({});

  const { settings, performer, credits, skills, media, links } = profileData;

  // Get accent color from settings or default to teal
  const accentColor = settings?.accent_color || '#00D9C0';

  // Shared hooks
  const { headshots, aboutPhotos, galleryPhotos } = useMediaFiltering(media);
  const { galleryGridStyle } = useGalleryConfig(settings);
  const { activeCreditsTab, setActiveCreditsTab, creditCategories } = useCreditsTab(credits);

  // Profile display data
  const displayName =
    performer?.display_name || basicUser?.full_name || basicUser?.username || 'Unknown';
  const tagline =
    performer?.tagline || performer?.job_title || basicUser?.industry || 'Actor & Model';
  const bio = performer?.bio || basicUser?.bio || '';

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Format name with accent dot
  const formatNameWithAccent = (name) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (
        <>
          {parts.slice(0, -1).join(' ')}
          <span className="bold-name-dot" style={{ color: accentColor }}>
            .
          </span>{' '}
          {parts[parts.length - 1]}
        </>
      );
    }
    return name;
  };

  return (
    <div className="bold-template" style={{ '--accent-color': accentColor }}>
      {/* Navigation */}
      <nav className="bold-nav">
        <div className="bold-nav-links">
          <button
            className={activeSection === 'home' ? 'active' : ''}
            onClick={() => scrollToSection('home')}
            style={activeSection === 'home' ? { color: accentColor } : {}}
          >
            Home
          </button>
          <button
            className={activeSection === 'about' ? 'active' : ''}
            onClick={() => scrollToSection('about')}
            style={activeSection === 'about' ? { color: accentColor } : {}}
          >
            About
          </button>
          {settings?.media_mode === 'separate' ? (
            <>
              <button
                className={activeSection === 'showreels' ? 'active' : ''}
                onClick={() => scrollToSection('showreels')}
                style={activeSection === 'showreels' ? { color: accentColor } : {}}
              >
                Showreels
              </button>
              <button
                className={activeSection === 'gallery' ? 'active' : ''}
                onClick={() => scrollToSection('gallery')}
                style={activeSection === 'gallery' ? { color: accentColor } : {}}
              >
                Gallery
              </button>
            </>
          ) : settings?.media_mode === 'gallery_only' ? (
            <button
              className={activeSection === 'gallery' ? 'active' : ''}
              onClick={() => scrollToSection('gallery')}
              style={activeSection === 'gallery' ? { color: accentColor } : {}}
            >
              Gallery
            </button>
          ) : settings?.media_mode === 'videos_only' ? (
            <button
              className={activeSection === 'showreels' ? 'active' : ''}
              onClick={() => scrollToSection('showreels')}
              style={activeSection === 'showreels' ? { color: accentColor } : {}}
            >
              Showreels
            </button>
          ) : (
            <button
              className={activeSection === 'media' ? 'active' : ''}
              onClick={() => scrollToSection('media')}
              style={activeSection === 'media' ? { color: accentColor } : {}}
            >
              Media
            </button>
          )}
          {creditCategories.length > 0 && (
            <button
              className={activeSection === 'resume' ? 'active' : ''}
              onClick={() => scrollToSection('resume')}
              style={activeSection === 'resume' ? { color: accentColor } : {}}
            >
              Resume
            </button>
          )}
          <button
            className={activeSection === 'contact' ? 'active' : ''}
            onClick={() => scrollToSection('contact')}
            style={activeSection === 'contact' ? { color: accentColor } : {}}
          >
            Representation
          </button>
        </div>
        {isOwnProfile && (
          <button className="bold-nav-edit" onClick={onEdit}>
            Edit
          </button>
        )}
      </nav>

      {/* Hero Section */}
      <section className="bold-hero" ref={(el) => (sectionRefs.current['home'] = el)}>
        {headshots.length > 0 ? (
          <div
            className={`bold-hero-photo-grid bold-hero-photo-grid-${Math.min(headshots.length, 4)}`}
          >
            {headshots.slice(0, 4).map((photo, i) => (
              <div key={photo.id} className="bold-hero-photo-cell">
                <img
                  src={getMediaUrl(photo.url)}
                  alt={displayName}
                  style={{
                    objectPosition: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                    transform:
                      (photo.zoom ?? 100) > 100 ? `scale(${(photo.zoom ?? 100) / 100})` : undefined,
                    transformOrigin: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                  }}
                />
                {isOwnProfile && onPhotoClick && (
                  <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span>Change</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : basicUser?.profile_image ? (
          <img src={basicUser.profile_image} alt={displayName} className="bold-hero-image" />
        ) : (
          <div className="bold-hero-placeholder">
            <span>{displayName.charAt(0).toUpperCase()}</span>
          </div>
        )}
        <div className="bold-hero-overlay">
          <h1 className="bold-hero-name">{formatNameWithAccent(displayName)}</h1>
          <p className="bold-hero-tagline">{tagline}</p>
        </div>
      </section>

      {/* About Section */}
      <section className="bold-about" ref={(el) => (sectionRefs.current['about'] = el)}>
        <div
          className="bold-about-image"
          style={{ '--accent-overlay': accentColor }}
        >
          {aboutPhotos.length > 0 ? (
            <div
              className={`bold-about-photo-grid bold-about-photo-grid-${Math.min(aboutPhotos.length, 4)}`}
            >
              {aboutPhotos.slice(0, 4).map((photo) => (
                <div key={photo.id} className="bold-about-photo-cell">
                  <img
                    src={getMediaUrl(photo.url)}
                    alt={displayName}
                    style={{
                      objectPosition: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                      transform:
                        (photo.zoom ?? 100) > 100
                          ? `scale(${(photo.zoom ?? 100) / 100})`
                          : undefined,
                      transformOrigin: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                    }}
                  />
                  {isOwnProfile && onPhotoClick && (
                    <div className="photo-edit-overlay" onClick={() => onPhotoClick('about')}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      <span>Change</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : basicUser?.profile_image ? (
            <img src={basicUser.profile_image} alt={displayName} />
          ) : (
            <div className="bold-about-placeholder">{displayName.charAt(0).toUpperCase()}</div>
          )}
        </div>
        <div className="bold-about-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 className="bold-section-title">ABOUT ME!</h2>
            <VisibilityToggle
              section="about"
              hiddenSections={hiddenSections}
              onToggleSection={onToggleSection}
            />
          </div>
          {!hiddenSections.includes('about') || onToggleSection ? (
            <>
              {hiddenSections.includes('about') && onToggleSection ? (
                <div
                  className="bold-section-hidden-placeholder"
                  style={{ opacity: 0.4, fontStyle: 'italic', padding: '20px 0' }}
                >
                  This section is hidden from visitors
                </div>
              ) : (
                <>
                  <div className="bold-about-text">
                    {bio ? (
                      bio.split('\n\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)
                    ) : (
                      <p>Add your bio in the profile settings to tell your story.</p>
                    )}
                  </div>
                  {creditCategories.length > 0 && (
                    <button className="bold-btn" onClick={() => scrollToSection('resume')}>
                      See Resume
                    </button>
                  )}
                </>
              )}
            </>
          ) : null}
        </div>
      </section>

      {settings?.media_mode === 'separate' ? (
        <>
          {/* Separate: Showreels Section */}
          <section className="bold-reels" ref={(el) => (sectionRefs.current['showreels'] = el)}>
            <div className="bold-reels-background"></div>
            {media && media.filter((m) => m.media_type === 'video').length > 0 ? (
              <>
                <div className="bold-reels-content">
                  {creditCategories.slice(0, 3).map((category, index) => (
                    <button
                      key={category}
                      className="bold-reel-btn"
                      style={{
                        backgroundColor: index % 2 === 0 ? `${accentColor}22` : accentColor,
                        color: index % 2 === 0 ? accentColor : '#000000',
                      }}
                      onClick={() => {
                        setActiveCreditsTab(category);
                        scrollToSection('resume');
                      }}
                    >
                      {category.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className="bold-reels-videos">
                  {media
                    .filter((m) => m.media_type === 'video')
                    .slice(0, 3)
                    .map((item, index) => (
                      <div key={index} className="bold-reel-video">
                        {item.embed_url ? (
                          <iframe
                            src={item.embed_url}
                            title={item.title || 'Video'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        ) : (
                          <div className="bold-reel-placeholder">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        )}
                        {item.title && <p className="bold-reel-title">{item.title}</p>}
                      </div>
                    ))}
                </div>
              </>
            ) : null}
          </section>

          {/* Separate: Gallery Section */}
          <section
            className="bold-gallery"
            ref={(el) => (sectionRefs.current['gallery'] = el)}
            style={{ position: 'relative' }}
          >
            {isOwnProfile && onToggleGalleryEdit && (
              <button
                className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
                onClick={onToggleGalleryEdit}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
              </button>
            )}
            {galleryEditMode && onGalleryConfigChange ? (
              <GalleryGridEditor
                photos={galleryPhotos}
                config={galleryConfig}
                onChange={onGalleryConfigChange}
                onReorder={onGalleryReorder}
                onCropPhoto={onGalleryPhotoCrop}
                theme="bold"
              />
            ) : (
              <div className="bold-gallery-grid" style={galleryGridStyle}>
                {galleryPhotos.length > 0
                  ? galleryPhotos.map((item) => (
                      <div key={item.id} className="bold-gallery-item">
                        <img
                          src={getMediaUrl(item.url)}
                          alt={item.title || 'Gallery image'}
                          style={{
                            objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                            transform:
                              (item.zoom ?? 100) > 100
                                ? `scale(${(item.zoom ?? 100) / 100})`
                                : undefined,
                            transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          }}
                        />
                        {item.title && (
                          <div className="bold-gallery-item-overlay">
                            <h3>{item.title}</h3>
                          </div>
                        )}
                        {isOwnProfile && onGalleryPhotoCrop && (
                          <div
                            className="gallery-photo-crop-overlay"
                            onClick={(e) => onGalleryPhotoCrop(item, e)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                              <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                            </svg>
                            <span>Crop</span>
                          </div>
                        )}
                      </div>
                    ))
                  : null}
              </div>
            )}
          </section>
        </>
      ) : settings?.media_mode === 'gallery_only' ? (
        <section
          className="bold-gallery"
          ref={(el) => (sectionRefs.current['gallery'] = el)}
          style={{ position: 'relative' }}
        >
          {isOwnProfile && onToggleGalleryEdit && (
            <button
              className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
              onClick={onToggleGalleryEdit}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
              {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
            </button>
          )}
          {galleryEditMode && onGalleryConfigChange ? (
            <GalleryGridEditor
              photos={galleryPhotos}
              config={galleryConfig}
              onChange={onGalleryConfigChange}
              onReorder={onGalleryReorder}
              onCropPhoto={onGalleryPhotoCrop}
              theme="bold"
            />
          ) : (
            <div className="bold-gallery-grid" style={galleryGridStyle}>
              {galleryPhotos.length > 0
                ? galleryPhotos.map((item) => (
                    <div key={item.id} className="bold-gallery-item">
                      <img
                        src={getMediaUrl(item.url)}
                        alt={item.title || 'Gallery image'}
                        style={{
                          objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          transform:
                            (item.zoom ?? 100) > 100
                              ? `scale(${(item.zoom ?? 100) / 100})`
                              : undefined,
                          transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                        }}
                      />
                      {item.title && (
                        <div className="bold-gallery-item-overlay">
                          <h3>{item.title}</h3>
                        </div>
                      )}
                      {isOwnProfile && onGalleryPhotoCrop && (
                        <div
                          className="gallery-photo-crop-overlay"
                          onClick={(e) => onGalleryPhotoCrop(item, e)}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                          </svg>
                          <span>Crop</span>
                        </div>
                      )}
                    </div>
                  ))
                : Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="bold-gallery-item bold-gallery-placeholder">
                      <div className="bold-gallery-placeholder-content">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                        </svg>
                        <p>Add photos in settings</p>
                      </div>
                    </div>
                  ))}
            </div>
          )}
        </section>
      ) : settings?.media_mode === 'videos_only' ? (
        <section className="bold-reels" ref={(el) => (sectionRefs.current['showreels'] = el)}>
          <div className="bold-reels-background"></div>
          {media && media.filter((m) => m.media_type === 'video').length > 0 ? (
            <div className="bold-reels-videos">
              {media
                .filter((m) => m.media_type === 'video')
                .slice(0, 3)
                .map((item, index) => (
                  <div key={index} className="bold-reel-video">
                    {item.embed_url ? (
                      <iframe
                        src={item.embed_url}
                        title={item.title || 'Video'}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div className="bold-reel-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="bold-reel-title">{item.title}</p>}
                  </div>
                ))}
            </div>
          ) : (
            <div style={{ padding: '60px 40px', textAlign: 'center', color: '#999' }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                style={{ marginBottom: '16px', opacity: 0.5 }}
              >
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
              <p style={{ fontSize: '16px', margin: 0 }}>
                No videos added yet. Add videos in profile settings.
              </p>
            </div>
          )}
        </section>
      ) : (
        /* Together: Combined Media Section — videos then gallery in separate sections */
        <>
          {media && media.filter((m) => m.media_type === 'video').length > 0 && (
            <section className="bold-reels" ref={(el) => (sectionRefs.current['media'] = el)}>
              <div className="bold-reels-background"></div>
              <div className="bold-reels-videos">
                {media
                  .filter((m) => m.media_type === 'video')
                  .slice(0, 3)
                  .map((item, index) => (
                    <div key={index} className="bold-reel-video">
                      {item.embed_url ? (
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <div className="bold-reel-placeholder">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                      {item.title && <p className="bold-reel-title">{item.title}</p>}
                    </div>
                  ))}
              </div>
            </section>
          )}
          <section
            className="bold-gallery"
            ref={(el) => {
              if (!sectionRefs.current['media']) sectionRefs.current['media'] = el;
            }}
            style={{ position: 'relative' }}
          >
            {isOwnProfile && onToggleGalleryEdit && (
              <button
                className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
                onClick={onToggleGalleryEdit}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
              </button>
            )}
            {galleryEditMode && onGalleryConfigChange ? (
              <GalleryGridEditor
                photos={galleryPhotos}
                config={galleryConfig}
                onChange={onGalleryConfigChange}
                onReorder={onGalleryReorder}
                onCropPhoto={onGalleryPhotoCrop}
                theme="bold"
              />
            ) : (
              <div className="bold-gallery-grid" style={galleryGridStyle}>
                {galleryPhotos.length > 0
                  ? galleryPhotos.map((item) => (
                      <div key={item.id} className="bold-gallery-item">
                        <img
                          src={getMediaUrl(item.url)}
                          alt={item.title || 'Gallery image'}
                          style={{
                            objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                            transform:
                              (item.zoom ?? 100) > 100
                                ? `scale(${(item.zoom ?? 100) / 100})`
                                : undefined,
                            transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          }}
                        />
                        {item.title && (
                          <div className="bold-gallery-item-overlay">
                            <h3>{item.title}</h3>
                          </div>
                        )}
                        {isOwnProfile && onGalleryPhotoCrop && (
                          <div
                            className="gallery-photo-crop-overlay"
                            onClick={(e) => onGalleryPhotoCrop(item, e)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                              <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                            </svg>
                            <span>Crop</span>
                          </div>
                        )}
                      </div>
                    ))
                  : null}
              </div>
            )}
          </section>
        </>
      )}

      {/* Resume Section */}
      {creditCategories.length > 0 && (
        <section className="bold-resume" ref={(el) => (sectionRefs.current['resume'] = el)}>
          <div className="bold-resume-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 className="bold-resume-title">Resume</h2>
              <VisibilityToggle
                section="resume"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {performer?.resume_pdf_url && (
              <a
                href={getMediaUrl(performer.resume_pdf_url)}
                download
                className="bold-resume-download"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download Resume
              </a>
            )}
          </div>

          {hiddenSections.includes('resume') && onToggleSection ? (
            <div
              className="bold-section-hidden-placeholder"
              style={{ opacity: 0.4, fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}
            >
              This section is hidden from visitors
            </div>
          ) : !hiddenSections.includes('resume') ? (
            <div className="bold-resume-content">
              <div className="bold-credits-tabs">
                {creditCategories.map((category) => (
                  <button
                    key={category}
                    className={`bold-credits-tab ${activeCreditsTab === category ? 'active' : ''}`}
                    onClick={() => setActiveCreditsTab(category)}
                    style={
                      activeCreditsTab === category
                        ? { borderColor: accentColor, color: accentColor }
                        : {}
                    }
                  >
                    {category}
                  </button>
                ))}
              </div>

              {activeCreditsTab && credits[activeCreditsTab] && (
                <table className="bold-credits-table">
                  <thead>
                    <tr>
                      <th>PRODUCTION</th>
                      <th>ROLE</th>
                      <th>COMPANY</th>
                      <th>DIRECTOR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credits[activeCreditsTab].map((credit, index) => (
                      <tr key={index}>
                        <td>
                          <Link
                            to={`/production?name=${encodeURIComponent(credit.production)}${credit.company ? `&company=${encodeURIComponent(credit.company)}` : ''}`}
                            className="credit-production-link"
                          >
                            {credit.production}
                          </Link>
                        </td>
                        <td>{credit.role}</td>
                        <td>{credit.company || ''}</td>
                        <td>{credit.director || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Skills */}
              {skills && Object.keys(skills).length > 0 && (
                <div className="bold-skills-section">
                  <h3 className="bold-skills-title">Skills & Training</h3>
                  <div className="bold-skills-grid">
                    {Object.entries(skills).map(([category, skillList]) => (
                      <div key={category} className="bold-skills-category">
                        <span className="bold-skills-category-name">{category}</span>
                        <span className="bold-skills-category-list">
                          {Array.isArray(skillList)
                            ? skillList.map((s) => s.skill || s.name || s).join(', ')
                            : String(skillList)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills (flat array fallback) */}
              {skills && Array.isArray(skills) && skills.length > 0 && (
                <div className="bold-skills-section">
                  <h3 className="bold-skills-title">Skills & Training</h3>
                  <div className="bold-skills-tags">
                    {skills.map((skill, i) => (
                      <span key={i} className="bold-skill-tag">
                        {skill.skill || skill.name || skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {performer?.resume_url && (
                <a
                  href={performer.resume_url}
                  download
                  className="bold-download-btn"
                  style={{ backgroundColor: accentColor }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                  </svg>
                  Download Resume PDF
                </a>
              )}
            </div>
          ) : null}
        </section>
      )}

      {/* Contact / Info Section */}
      {(!hiddenSections.includes('contact') || onToggleSection) &&
        (links?.length > 0 || performer?.representation || basicUser?.location) && (
          <section className="bold-contact" ref={(el) => (sectionRefs.current['contact'] = el)}>
            <div className="bold-contact-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 className="bold-resume-title">Contact</h2>
                <VisibilityToggle
                  section="contact"
                  hiddenSections={hiddenSections}
                  onToggleSection={onToggleSection}
                />
              </div>
            </div>

            {hiddenSections.includes('contact') && onToggleSection ? (
              <div
                className="bold-section-hidden-placeholder"
                style={{
                  opacity: 0.4,
                  fontStyle: 'italic',
                  padding: '40px 0',
                  textAlign: 'center',
                }}
              >
                This section is hidden from visitors
              </div>
            ) : (
              <>
                {performer?.contact_blurb && (
                  <p className="bold-contact-blurb">{performer.contact_blurb}</p>
                )}
                <div className="bold-contact-grid">
                  {performer?.representation && (
                    <div className="bold-contact-item">
                      <span className="bold-contact-label">Representation</span>
                      <span className="bold-contact-value">{performer.representation}</span>
                    </div>
                  )}
                  {basicUser?.location && (
                    <div className="bold-contact-item">
                      <span className="bold-contact-label">Location</span>
                      <span className="bold-contact-value">{basicUser.location}</span>
                    </div>
                  )}
                  {links &&
                    links
                      .filter((l) => l.is_public !== 0)
                      .map((link, index) => (
                        <div key={index} className="bold-contact-item">
                          <span className="bold-contact-label">{link.label}</span>
                          <span className="bold-contact-value">
                            {link.url.startsWith('http') ? (
                              <a href={link.url} target="_blank" rel="noopener noreferrer">
                                {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                              </a>
                            ) : (
                              link.url
                            )}
                          </span>
                        </div>
                      ))}
                </div>
              </>
            )}
          </section>
        )}
    </div>
  );
}

export default BoldTemplate;
